import { Injectable } from '@angular/core';
import { map, Observable, of, retry, timer } from 'rxjs';
import { Apollo, gql } from 'apollo-angular';
import { ClinicInterface } from '../../../shared/models/clinic.interface';
import { UserInterface } from '../../../shared/models/user.interface';
import { SccStudyDataInterface } from './scc-study-data.interface';
import { GetStudyDetailsQuery } from '../../../shared/services/studies.service';

@Injectable({
  providedIn: 'root'
})
export class SccService {

  constructor(private readonly apollo: Apollo) {}

  // eslint-disable-next-line @typescript-eslint/ban-types
  getDogByTranspondeurId(transpondeurId?: string | null): Observable<SccDogWithLastDateDto | undefined> {
    if (!transpondeurId) {
      return of(undefined);
    }

    return this.apollo.query<{ sccDogFromTranspondeurId: SccDogWithLastDateDto }>({
      query: gql`query getDogScc($transpondeurId: String!) {
        sccDogFromTranspondeurId(transpondeurId: $transpondeurId) {
          sccDog {
            id
            nom
            race
            maladies
            options
            dateNaissance
            transpondeur
            sexe
            registryStatus
          }
          errorTitle
          errorDetail
          sccStudyDatas {
            id
            createdAt
            dateReviewed
            reference
            status
            isElbow
            isHip
            isAnfh
            isAs
            isPs
            isMvd
            isDcm
            isVa
            isLtv
            isOcd
            isInternalAppeal
            appealDiseases
            dysplasiaElbow
            dysplasiaHip
          }
        }
      }`,
      variables: {
        transpondeurId
      },
      fetchPolicy: 'network-only'
    }).pipe(map(res => res.data.sccDogFromTranspondeurId));
  }

  getBreeds(): Observable<SccBreed[]> {
    return this.apollo.query<{ sccBreeds: SccBreed[] }>({
      query: gql`query getSccBreeds { sccBreeds { id name } }`,
      fetchPolicy: 'cache-first'
    }).pipe(map(r => r.data.sccBreeds ?? []));
  }

  createDog(dto: SccCreateDogInput): Observable<SccDog | undefined> {
    return this.apollo.mutate<{ createSccDog: SccDog }>({
      mutation: gql`mutation createSccDog($dto: SccCreateDogDtoInput!) {
        createSccDog(dto: $dto) {
          id nom sexe dateNaissance lof transpondeur race variete couleur maladies options registryStatus
        }
      }`,
      variables: { dto }
    }).pipe(map(r => r.data?.createSccDog));
  }

  register(form: SccRegisterDto): Observable<SccRegisterResultDto | undefined> {
    return this.apollo.mutate<{ registerScc: SccRegisterResultDto }>({
      mutation: gql`mutation registerScc($dto: SccRegisterDtoInput!) {
        registerScc(dto: $dto) {
          token
          clinic {
            id
          }
          needPaymentMethod
        }
      }`,
      variables: {
        dto: form
      }
    }).pipe(map(res => res.data?.registerScc ));
  }

  confirmSccClinic(dto: ConfirmSccClinicDto): Observable<SccRegisterResultDto | undefined> {
    return this.apollo.mutate<{ confirmSccClinic: SccRegisterResultDto }>({
      mutation: gql`mutation confirmSccClinic($dto: ConfirmSccClinicDtoInput!) {
        confirmSccClinic(dto: $dto) {
          token
          needPaymentMethod
          clinic {
            id
            name
            hasPaymentMethod
          }
        }
      }`,
      variables: { dto }
    }).pipe(map(res => res.data?.confirmSccClinic));
  }

  uploadStudy(form: SccUploadStudyDto, files: File[]): Observable<{ nbFiles: number; studyData: SccStudyDataInterface } | undefined> {
    return this.apollo.mutate<{ uploadStudiesScc: { nbFiles: number; studyData: SccStudyDataInterface } }>({
      mutation: gql`mutation uploadStudiesScc($dto: UploadStudiesSccDtoInput!, $files: [Upload!]) {
        uploadStudiesScc(dto: $dto, files: $files) {
          nbFiles
          studyData {
            id
            createdAt
            orthancInstanceUid
            dogTranspondeurId
            breed
            isElbow
            isHip
            isAnfh
            isAs
            isPs
            isMvd
            isDcm
            isVa
            isLtv
            isOcd
            sedative
            reference
            price
            status
            name
            sex
            birthDate
            uploader {
              id
              firstName
              lastName
              orderNum
              email
            }
          }
        }
      }`,
      variables: {
        dto: form,
        files
      },
      context: {
        useMultipart: true
      },
      refetchQueries: [
        'getStudiesCounter',
        'getStudiesScc'
      ]
    }).pipe(map(res => res.data?.uploadStudiesScc ));
  }

  uploadStudyChangeStatus(id: number, isConfirmed: boolean, seriesSubject: SerieSubjectUpdate[]): Observable<SccStudyDataInterface | undefined> {
    return this.apollo.mutate<{ uploadStudiesSccChangeStatus: SccStudyDataInterface }>({
      mutation: gql`mutation uploadStudiesSccChangeStatus($studySccId: Long!, $isConfirmed: Boolean!, $seriesSubject: [UpdateSerieSubjectDtoInput]) {
        uploadStudiesSccChangeStatus(studySccId: $studySccId, isConfirmed: $isConfirmed, seriesSubject: $seriesSubject) {
          id
          uploadReport {
            id
            originalName
            path
          }
        }
      }`,
      variables: {
        studySccId: id,
        isConfirmed,
        seriesSubject
      },
      refetchQueries: [
        'getStudiesCounter',
        'getStudiesScc'
      ]
    }).pipe(map(res => res.data?.uploadStudiesSccChangeStatus ));
  }

  updateReport(studyId: number, studySccId: number, dto: SccUpdateReportDto, isConfirmation: boolean): Observable<SccStudyDataInterface | undefined> {
    return this.apollo.mutate<{ updateSccReport: SccStudyDataInterface }>({
      mutation: gql`mutation updateSccReport($studySccId: Long!, $dto: SccReportDtoInput!, $isConfirmation: Boolean!) {
        updateSccReport(studySccId: $studySccId, dto: $dto, isConfirmation: $isConfirmation) {
          id
          status
          myReportFormData {
              id
              report {
                  id
                  originalName
                  path
              }
          }
        }
      }`,
      variables: {
        studySccId,
        dto,
        isConfirmation
      },
      refetchQueries: [{
        query: GetStudyDetailsQuery,
        variables: { studyId }
      }, 'getStudiesCounter',
      'getStudiesScc']
    }).pipe(
      retry({ count: 3, delay: (_, retryCount) => timer([1000, 3000, 8000][retryCount - 1] ?? 8000) }),
      map(res => res.data?.updateSccReport)
    );
  }

  updateReportWithAttachment(studyId: number, studySccId: number, dto: SccUpdateReportDto, isConfirmation: boolean, reportAttachment: File): Observable<SccStudyDataInterface | undefined> {
    return this.apollo.mutate<{ updateSccReport: SccStudyDataInterface }>({
      mutation: gql`mutation updateSccReport($studySccId: Long!, $dto: SccReportDtoInput!, $isConfirmation: Boolean!, $reportAttachment: Upload) {
      updateSccReport(studySccId: $studySccId, dto: $dto, isConfirmation: $isConfirmation, reportAttachment: $reportAttachment) {
        id
        status
        myReportFormData {
            id
            report {
                id
                originalName
                path
            }
        }
      }
    }`,
      variables: {
        studySccId,
        dto,
        isConfirmation,
        reportAttachment
      },
      context: {
        useMultipart: true
      },
      refetchQueries: [{
        query: GetStudyDetailsQuery,
        variables: { studyId }
      }, 'getStudiesCounter',
      'getStudiesScc']
    }).pipe(
      retry({ count: 3, delay: (_, retryCount) => timer([1000, 3000, 8000][retryCount - 1] ?? 8000) }),
      map(res => res.data?.updateSccReport ));
  }

  sendNoAgreement(studyId: number, studySccId: number): Observable<SccStudyDataInterface | undefined> {
    return this.apollo.mutate<{ sccNoAgreement: SccStudyDataInterface }>({
      mutation: gql`mutation sccNoAgreement($studySccId: Long!) {
        sccNoAgreement(studySccId: $studySccId) {
          id
          status
          dateReviewedDisagreement
        }
      }`,
      variables: {
        studySccId
      },
      refetchQueries: [{
        query: GetStudyDetailsQuery,
        variables: { studyId }
      }, 'getStudiesCounter',
      'getStudiesScc'],
    }).pipe(map(res => res.data?.sccNoAgreement));
  }

  cancel(studyId: number, sccStudyId: number, dismissedReason?: string, dismissedMessage?: string): Observable<SccStudyDataInterface | undefined> {
    return this.apollo.mutate<{ sccNoAgreement: SccStudyDataInterface }>({
      mutation: gql`mutation cancelSccStudy($studySccId: Long!, $dismissedReason: String, $dismissedMessage: String) {
        cancelSccStudy(studySccId: $studySccId, dismissedReason: $dismissedReason, dismissedMessage: $dismissedMessage) {
          id
          status
          dateCanceled
        }
      }`,
      variables: {
        studySccId: sccStudyId,
        dismissedReason,
        dismissedMessage
      },
      refetchQueries: [{
        query: GetStudyDetailsQuery,
        variables: { studyId }
      }, 'getStudiesCounter',
      'getStudiesScc']
    }).pipe(map(res => res.data?.sccNoAgreement));
  }

  deleteSccStudy(sccStudyId: number): Observable<boolean | undefined> {
    return this.apollo.mutate<{ deleteSccStudy: boolean }>({
      mutation: gql`mutation deleteSccStudy($sccStudyId: Long!) {
        deleteSccStudy(sccStudyId: $sccStudyId)
      }`,
      variables: {
        sccStudyId
      },
      refetchQueries: ['getStudiesCounter', 'getStudiesScc']
    }).pipe(map(res => res.data?.deleteSccStudy));
  }

  getPrice(): Observable<number> {
    return this.apollo.query<{ sccProductPrice: number }>({
      query: gql`
        query getSccProductPrice {
          sccProductPrice
        }
      `
    }).pipe(map(r => r.data.sccProductPrice));
  }

  getSccReviewers(): Observable<UserInterface[]> {
    return this.apollo.query<{ sccReviewers: UserInterface[] }>({
      query: gql`
        query getSccReviewers {
          sccReviewers {
            id
            orderNum
            firstName
            lastName
            isSccAppealReader
          }
        }
      `
    }).pipe(map(r => r.data.sccReviewers));
  }

  getSccDismissedMotifs(): Observable<SccDismissedMotifInterface[]> {
    // Le champ GraphQL est `sccDismissedMotifs` (HotChocolate retire le préfixe Get du
    // nom de méthode côté serveur) — ne pas confondre avec le nom de l'opération.
    return this.apollo.query<{ sccDismissedMotifs: SccDismissedMotifInterface[] }>({
      query: gql`
        query getSccDismissedMotifs {
          sccDismissedMotifs {
            id
            label
          }
        }
      `
    }).pipe(map(r => r.data.sccDismissedMotifs));
  }

  changePaymentStatusAndReviewers(studyId: number, studySccId: number, dto: SccStudyPaymentDto): Observable<SccStudyDataInterface | undefined> {
    return this.apollo.mutate<{ changePaymentStatusAndReviewers: SccStudyDataInterface }>({
      mutation: gql`
        mutation changePaymentStatusAndReviewers($studySccId: Long!, $dto: SccStudyPaymentDtoInput!) {
          changePaymentStatusAndReviewers(studySccId: $studySccId, dto: $dto) {
            id
          }
        }
      `,
      variables: {
        studySccId,
        dto
      },
      refetchQueries: [{
        query: GetStudyDetailsQuery,
        variables: { studyId }
      }, 'getStudiesCounter',
      'getStudiesScc']
    }).pipe(map(r => r.data?.changePaymentStatusAndReviewers));
  }

  switchToReviewDiscussion(studyId: number, studySccId: number, dto: SccAppealDto): Observable<SccStudyDataInterface | undefined> {
    return this.apollo.mutate<{ switchToReviewDiscussion: SccStudyDataInterface }>({
      mutation: gql`
        mutation switchToReviewDiscussion($studySccId: Long!, $dto: SccAppealDtoInput!) {
          switchToReviewDiscussion(studySccId: $studySccId, dto: $dto) {
            id
            appealDiseases
          }
        }
      `,
      variables: {
        studySccId,
        dto
      },
      refetchQueries: [{
        query: GetStudyDetailsQuery,
        variables: { studyId }
      }, 'getStudiesCounter',
      'getStudiesScc']
    }).pipe(map(r => r.data?.switchToReviewDiscussion));
  }

  generateUploadUrlFromMypacs(clinicId: number, instances: string[]): Observable<string | undefined> {
    return this.apollo.query<{ generateUploadUrlFromMypacs: string }>({
      query: gql`
        query generateUploadUrlFromMypacs($clinicId: Long!, $instances: [String]!) {
          generateUploadUrlFromMypacs(clinicId: $clinicId, instances: $instances)
        }
      `,
      variables: {
        clinicId,
        instances
      }
    }).pipe(map(r => r.data?.generateUploadUrlFromMypacs));
  }

  recycleStudy(studySccId: number, formsUserId: number): Observable<SccStudyDataInterface | undefined> {
    return this.apollo.mutate<{ recycleStudyScc: SccStudyDataInterface }>({
      mutation: gql`
        mutation recycleStudyScc($studySccId: Long!, $formsUserId: Long!) {
          recycleStudyScc(studySccId: $studySccId, formsUserId: $formsUserId) {
            id
            dogTranspondeurId
            canReviewerBeChanged
            canBeCanceled
            isEnded
            isEndedOk
            createdAt
            updatedAt
            isElbow
            isHip
            isAnfh
            isAs
            isPs
            isMvd
            isDcm
            isVa
            isLtv
            isOcd
            breed
            sedative
            reference
            price
            status
            datePayedByClinic
            datePayedByOwner
            dateReviewed
            dateReviewedDisagreement
            dateEnded
            dateCanceled
            reviewAllowed
            reviewTimeout
            birthDate
            name
            uploadReport {
              id
              path
            }
            report {
              id
              path
            }
            reviewer1 {
              id
              firstName
              lastName
              orderNum
            }
            reviewer2 {
              id
              firstName
              lastName
              orderNum
            }
            uploader {
              id
              firstName
              lastName
              orderNum
            }
            reviewer1FormData {
              id
              dateConfirmed
              isActive
              report {
                id
                path
              }
            }
            reviewer2FormData {
              id
              dateConfirmed
              isActive
              report {
                id
                path
              }
            }
          }
        }
      `,
      variables: {
        studySccId,
        formsUserId
      }
    }).pipe(map(r => r.data?.recycleStudyScc));
  }

  regenerateSccReportPdf(studySccId: number, formId: number): Observable<unknown> {
    return this.apollo.mutate({
      mutation: gql`mutation regenerateSccReportPdf($studySccId: Long!, $formId: Long!) {
        regenerateSccReportPdf(studySccId: $studySccId, formId: $formId) {
          id
        }
      }`,
      variables: { studySccId, formId }
    });
  }

  regenerateSccUploadPdf(studySccId: number): Observable<unknown> {
    return this.apollo.mutate({
      mutation: gql`mutation regenerateSccUploadPdf($studySccId: Long!) {
        regenerateSccUploadPdf(studySccId: $studySccId) {
          id
        }
      }`,
      variables: { studySccId }
    });
  }

  checkInternalAppealEligibility(transponder: string, disease: string): Observable<InternalAppealEligibilityDto | undefined> {
    return this.apollo.query<{ checkInternalAppealEligibility: InternalAppealEligibilityDto }>({
      query: gql`
        query checkInternalAppealEligibility($transponder: String!, $disease: String!) {
          checkInternalAppealEligibility(transponder: $transponder, disease: $disease) {
            isEligible
            originalStudyId
            originalStudyReference
            originalResult
            originalDate
            reason
          }
        }
      `,
      variables: {
        transponder,
        disease
      },
      fetchPolicy: 'network-only'
    }).pipe(map(r => r.data?.checkInternalAppealEligibility));
  }

  getAppealableDiseases(transponder: string): Observable<AppealableDiseaseDto[]> {
    return this.apollo.query<{ getAppealableDiseases: AppealableDiseaseDto[] }>({
      query: gql`
        query getAppealableDiseases($transponder: String!) {
          getAppealableDiseases(transponder: $transponder) {
            disease
            diseaseName
            originalResult
            originalDate
            originalStudyId
            originalStudyReference
            canAppeal
            reason
          }
        }
      `,
      variables: {
        transponder
      },
      fetchPolicy: 'network-only'
    }).pipe(map(r => r.data?.getAppealableDiseases ?? []));
  }
}

export interface SccDogWithLastDateDto {
  sccDog?: SccDog;
  sccStudyDatas?: SccStudyDataInterface[];
  errorTitle?: string;
  errorDetail?: string;
}

export interface SccDog {
  id: number;
  nom: string;
  sexe: 'M' | 'F';
  dateNaissance: string;
  lof: string;
  tatouage?: string;
  transpondeur: string;
  race: string;
  variete: string;
  couleur: string;
  maladies: string[];
  options: string[];
  registryStatus?: SccRegistryStatus;
}

export type SccRegistryStatus = 'LOF_ICAD' | 'ICAD_ONLY';

export interface SccBreed {
  id: number;
  name: string;
}

export interface SccCreateDogInput {
  identifiant: string;
  name: string;
  dateNaissance: string;
  sexe: 'M' | 'F';
  idBreed: number;
}

export interface ConfirmSccClinicDto {
  clinicId?: number;
  clinic?: {
    name: string;
    alias: string;
    address1: string;
    address2?: string;
    postalCode: string;
    city: string;
    email: string;
    phone: string;
    clinicTypeId: number;
  };
}

export interface SccRegisterDto {
  user: {
    ordinalNumber: string;
    email: string;
    firstName: string;
    lastName: string;
    password?: string;
  };
  clinicId?: number;
  clinic?: {
    alias: string;
    name: string;
    address1: string;
    address2?: string;
    postalCode: string;
    city: string;
    email: string;
    phone: string;
    clinicTypeId: number;
  };
}

export interface SccRegisterResultDto {
  user: UserInterface;
  clinic: ClinicInterface;
  token: string;
  needPaymentMethod?: boolean;
}

export interface SccUploadStudyDto {
  clinicId: number;
  dogTranspondeurId: string;
  isElbow: boolean;
  isHip: boolean;
  isAnfh: boolean;
  isAs: boolean;
  isPs: boolean;
  isMvd: boolean;
  isDcm: boolean;
  isVa: boolean;
  isLtv: boolean;
  isOcd: boolean;
  sedative: string;
  animalIsCorrect: boolean;
  consentIa: boolean;
  transpondeurInDicomTags: boolean;
  ownerMail: string;
  ownerPhoneNumber: string;
  // eslint-disable-next-line @typescript-eslint/ban-types
  instances?: string[] | null;
  // Internal appeal fields
  isInternalAppeal?: boolean;
  originalStudyId?: number;
  appealDiseases?: string[];
}

export interface SccDismissedMotifInterface {
  id: number;
  label: string;
}

export interface SccUpdateReportDto {
  identificationComplete: boolean;
  identificationUnforgeable: boolean;
  lateralisedRadio: boolean;

  hipPelvicsSymmetry?: string;
  hipFemurParallel?: string;
  hipVisibleKneecaps?: boolean;
  hipMedianPositionedKneecaps?: boolean;

  hipJointRelationshipsRight?: 'Coaptation parfaite' | 'Coaptation acceptable' | 'Coaptation imparfaite' | 'Pincement de l\'interligne' | 'Sub-luxation' | 'Luxation';
  hipJointRelationshipsLeft?: 'Coaptation parfaite' | 'Coaptation acceptable' | 'Coaptation imparfaite' | 'Pincement de l\'interligne' | 'Sub-luxation' | 'Luxation';
  hipAcetabulumRight?: 'Normal' | 'Manque de profondeur' | 'Evasé' | 'Comblé' | 'Rebord acétabulaire cranial enserrant' | 'Rebord acétabulaire cranial ouvert';
  hipAcetabulumLeft?: 'Normal' | 'Manque de profondeur' | 'Evasé' | 'Comblé' | 'Rebord acétabulaire cranial enserrant' | 'Rebord acétabulaire cranial ouvert';
  hipPoscDensificationRight?: boolean;
  hipPoscDensificationLeft?: boolean;
  hipOsteophytoseAcetabularRight?: boolean;
  hipOsteophytoseAcetabularLeft?: boolean;
  hipOsteophytoseCaudalRight?: boolean;
  hipOsteophytoseCaudalLeft?: boolean;
  hipHeadAndNeckShapeVolumeRight?: boolean;
  hipHeadAndNeckShapeVolumeLeft?: boolean;
  hipHeadAndNeckOsteophytoseCephalicRight?: boolean;
  hipHeadAndNeckOsteophytoseCephalicLeft?: boolean;
  hipHeadAndNeckOsteophytoseFemoralLeft?: boolean;
  hipHeadAndNeckOsteophytoseFemoralRight?: boolean;
  hipHeadCenterPositionRight?: 'CTF médial par rapport au RAD' | 'CTF superposé au RAD' | 'CTF latéral par rapport au RAD';
  hipHeadCenterPositionLeft?: 'CTF médial par rapport au RAD' | 'CTF superposé au RAD' | 'CTF latéral par rapport au RAD';
  hipNorbergOlssonAngleRight?: 'Angle supérieur ou égal à 105°' | 'Angle compris entre 100° et 105°' | 'Angle inférieur à 100°' | 'Angle inférieur à 90°';
  hipNorbergOlssonAngleLeft?: 'Angle supérieur ou égal à 105°' | 'Angle compris entre 100° et 105°' | 'Angle inférieur à 100°' | 'Angle inférieur à 90°';
  hipCranialAcetabularCoverRight?: boolean;
  hipCranialAcetabularCoverLeft?: boolean;
  hipDorsalAcetabularCoverRight?: boolean;
  hipDorsalAcetabularCoverLeft?: boolean;

  hipResult?: 'A' | 'B' | 'C' | 'D' | 'E';

  elbowIncidenceExtensionRight?: boolean;
  elbowIncidenceExtensionLeft?: boolean;
  elbowIncidenceFlexionRight?: boolean;
  elbowIncidenceFlexionLeft?: boolean;
  elbowIncidenceRotationRight?: boolean;
  elbowIncidenceRotationLeft?: boolean;

  elbowAnomalyNonUnionRight?: boolean;
  elbowAnomalyNonUnionLeft?: boolean;
  elbowAnomalyCoronoidalFragmentationRight?: boolean;
  elbowAnomalyCoronoidalFragmentationLeft?: boolean;
  elbowAnomalyOsteochondriteRight?: boolean;
  elbowAnomalyOsteochondriteLeft?: boolean;
  elbowAnomalyArticularIncongruenceRight?: boolean;
  elbowAnomalyArticularIncongruenceLeft?: boolean;
  elbowAnomalyCalcificationRight?: boolean;
  elbowAnomalyCalcificationLeft?: boolean;
  elbowOsteoarthritisManifestationsOsteophytesEdgesRight?: 'Absence d\'ostéophyte visible aux marges articulaires' | 'Ostéophytes inférieurs à 2mm' | 'Ostéophytes entre 2 mm et 5 mm' | 'Ostéophytes supérieurs à 5 mm';
  elbowOsteoarthritisManifestationsOsteophytesEdgesLeft?: 'Absence d\'ostéophyte visible aux marges articulaires' | 'Ostéophytes inférieurs à 2mm' | 'Ostéophytes entre 2 mm et 5 mm' | 'Ostéophytes supérieurs à 5 mm';
  elbowOsteoarthritisManifestationsBoneDensificationRight?: boolean;
  elbowOsteoarthritisManifestationsBoneDensificationLeft?: boolean;
  elbowOsteoarthritisManifestationsOsteophytesMedialCompartmentRight?: boolean;
  elbowOsteoarthritisManifestationsOsteophytesMedialCompartmentLeft?: boolean;
  elbowOsteoarthritisManifestationsOsteophytesLateralProfilesRight?: boolean;
  elbowOsteoarthritisManifestationsOsteophytesLateralProfilesLeft?: boolean;

  elbowResult?: 'EDO' | 'SL' | 'ED1' | 'ED2' | 'ED3';

  anfhResult?: 'ANFH-0' | 'ANFH-1';
  asResult?: 'AS-0' | 'AS-1' | 'AS-2' | 'AS-3';
  psResult?: 'PS-0' | 'PS-1' | 'PS-2' | 'PS-3';
  mvdResult?: 'MVD-0' | 'MVD-1' | 'MVD-2' | 'MVD-3' | 'MVD-4';
  dcmResult?: 'DCM-0' | 'DCM-1' | 'DCM-2' | 'DCM-3';
  vaResult?: 'VA-0' | 'VA-1';
  ltvResult?: 'LTV-0' | 'LTV-1' | 'LTV-2' | 'LTV-3';
  ocdResult?: 'OCD-0' | 'OCD-1';

  comment?: string;
}

export interface SccStudyPaymentDto {
  isPaid: boolean;
  reviewerOrdinalNumbers: (string | undefined)[];
  reviewerIds?: (number | undefined)[];
}

export interface SccAppealDto {
  appealDiseases: string[];
  reviewerOrdinalNumbers: string[];
  reviewerIds?: number[];
}

export interface SerieSubjectUpdate {
  id: number;
  subject: 'ELBOW' | 'HIP' | 'OTHER';
}

export interface SccReportAttachment {
  attachment: ArrayBuffer;
  attachmentFileName: string;
  attachmentContentType: string;
}

export interface InternalAppealEligibilityDto {
  isEligible: boolean;
  originalStudyId?: number;
  originalStudyReference?: string;
  originalResult?: string;
  originalDate?: string;
  reason?: string;
}

export interface AppealableDiseaseDto {
  disease: string;
  diseaseName: string;
  originalResult?: string;
  originalDate?: string;
  originalStudyId: number;
  originalStudyReference: string;
  canAppeal: boolean;
  reason?: string;
}